import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { styles } from '@utils';
import Layout from '@components/layout';

import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import Hero from '@repo/ui-components/hero/Hero';
import GridCard from '@repo/ui-components/GridCard';
import GridCardContainer from '@repo/ui-components/GridCardContainer';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import PaginationArea from '@repo/ui-components/collections/PaginationArea';

import withTwoPassRendering from '@repo/utils/twoPassRendering';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';
import getMultipleRandomItemsFromArray from '@repo/utils/getMultipleRandomItemsFromArray';

import getInternalLink from '@components/utils/getInternalLink';
import GearTagLine from '@components/GearTagLine';
import HorizontalLine from '@components/HorizontalLine';
import {
  getHreflangValuesForPaginatedPages,
  LocalizedLink,
  getLocalizedPagePath,
} from '@repo/gatsby-localization-helpers';

import { useSportdogInternationalContext } from '@util/static-translations';

const GridCardWithTwoPass = withTwoPassRendering(GridCard);

const CategoryLink = styled(LocalizedLink)`
  text-transform: uppercase;
  color: ${styles.colors.orange[500]};
  text-decoration: underline;
  display: inline-block;
  width: max-content;

  &:hover {
    text-decoration: none;
  }
`;

const CategoriesText = styled.div`
  color: ${({ theme }) => theme.colors.defaultFont};
  margin-top: 1.625rem;

  h2 {
    text-transform: uppercase;
    display: block;
  }
`;

const useFeaturedPeople = people => {
  const [featuredPeople, setFeaturedPeople] = useState([]);

  useEffect(() => {
    (async () => {
      const ids = people.nodes.map(node => node._id) ?? [];
      const fourRandomIds = getMultipleRandomItemsFromArray(ids, 4);

      const promises = fourRandomIds.map(async id => {
        const nextPageUrl = `${window.location.origin}/static/json-data/people/${id}.json`;
        const result = await fetch(nextPageUrl);
        return result.json();
      });

      const values = await Promise.all(promises);
      setFeaturedPeople(values);
    })();
  }, [people]);

  return featuredPeople;
};

function HuntingTrainingTipsTemplate({ pageContext, data }) {
  const {
    previousPagePath,
    nextPagePath,
    pageCountByLanguage,
    linkHreflangValues,
    humanPageNumber,
    blogAuthor,
    blogCategory,
    shouldGetAuthors,
  } = pageContext;

  const pagedArticles = data.posts?.nodes || [];
  const pagedAuthors = data.authors?.nodes || [];
  const categories = data.categories?.nodes || [];

  const featuredPeople = useFeaturedPeople(data.people);

  const { tt, locale } = useSportdogInternationalContext();

  const breadcrumbs = [
    {
      text: tt(s => s.breadcrumbsCommon.home),
      link: `/`,
    },
    {
      text: tt(s => s.breadcrumbsCommon.trainingTips),
      link: `/hunting-training-tips`,
    },

    ...(blogCategory
      ? [
          {
            text: blogCategory.title,
            link: `/hunting-training-tips/${blogCategory.slug}`,
          },
        ]
      : []),

    ...(blogAuthor || shouldGetAuthors
      ? [
          {
            text: tt(s => s.breadcrumbsCommon.authors),
            link: `/hunting-training-tips/authors`,
          },
        ]
      : []),

    ...(blogAuthor
      ? [
          {
            text: blogAuthor.title,
            link: `/hunting-training-tips/authors/${blogAuthor.slug}`,
          },
        ]
      : []),
  ];

  const pageTitleModifier = (() => {
    if (shouldGetAuthors) return tt(s => s.articleListPage.metaTitleAuthors);

    if (blogAuthor) return blogAuthor.title;

    return null;
  })();

  const metaTitle = tt(s => s.articleListPage.metaTitle, {
    pageTitleModifier: pageTitleModifier
      ? ` - ${pageTitleModifier
          ?.split('-')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')}`
      : '',
  });

  const metaDescription = tt(s => s.articleListPage.metaDescription);

  return (
    <Layout
      headTagData={{
        title: metaTitle,
        description: metaDescription,
        meta: createOpenGraphTags(
          {
            title: metaTitle,
            description: metaDescription,
            url:
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath('/hunting-training-tips', locale),
            image: `${
              typeof window !== 'undefined' && window?.location?.origin
            }/training-tips/training-tips.jpg`,
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={getHreflangValuesForPaginatedPages({
        values: linkHreflangValues,
        humanPageNumber,
        pageCountByLanguage,
      })}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <PageWrapper>
        <Hero
          hero={{
            mediaItem: '/training-tips/training-tips.jpg',
            heroType: 'image',
            headline1: tt(s => s.articleListPage.heroHeadline1),
            headline2: tt(s => s.articleListPage.heroHeadline2),
            altText: tt(s => s.articleListPage.heroAltText),
            textAlignment: 'middleRight',
            mobileCrop: '31% 0',
          }}
          getInternalLink={getInternalLink}
        />

        <CategoriesText>
          <h2>{tt(s => s.articleListPage.categoriesHeader)}</h2>
          {categories
            .sort((catA, catB) => catA.title.localeCompare(catB.title))
            .map((category, index) => (
              <span key={category.title}>
                <CategoryLink
                  to={`/hunting-training-tips/${category.slug.current}`}
                >
                  {category.title}
                </CategoryLink>
                {categories.length - 1 !== index && ', '}
              </span>
            ))}
        </CategoriesText>

        <HorizontalLine />

        {pagedAuthors?.length > 0 && (
          <GridCardContainer>
            {pagedAuthors.map(author => (
              <GridCard
                key={author.title}
                item={{
                  ...author,
                  subtitle: `${author.relatedArticles.length} ${
                    author.relatedArticles.length > 1
                      ? tt(s => s.articleListPage.authorSubtitlePlural)
                      : tt(s => s.articleListPage.authorSubtitleSingular)
                  }`,
                  link: `/hunting-training-tips/authors/${author.slug.current}`,
                }}
              />
            ))}
          </GridCardContainer>
        )}

        {pagedArticles?.length > 0 && (
          <GridCardContainer>
            {pagedArticles.map(article => (
              <GridCard
                key={article.title}
                item={{
                  ...article,
                  subtitle: article?.author?.title
                    ? tt(s => s.articleListPage.articleSubtitle, {
                        authorTitle: article.author.title,
                      })
                    : tt(s => s.articleListPage.articleSubtitleDefault),
                  link: `/hunting-training-tips/${article.slug.current}`,
                }}
              />
            ))}
          </GridCardContainer>
        )}

        {(previousPagePath || nextPagePath) && (
          <PaginationArea
            previousPagePath={previousPagePath}
            nextPagePath={nextPagePath}
          />
        )}

        {featuredPeople?.length > 0 && (
          <>
            <h2>{tt(s => s.articleListPage.authorsHeader)}</h2>
            <GridCardContainer>
              {featuredPeople.map(person => (
                <GridCardWithTwoPass
                  key={person.title}
                  item={{
                    ...person,
                    subtitle: `${person.relatedArticles.length} ${
                      person.relatedArticles.length > 1
                        ? tt(s => s.articleListPage.authorSubtitlePlural)
                        : tt(s => s.articleListPage.authorSubtitleSingular)
                    }`,
                    link: `/hunting-training-tips/authors/${person.slug.current}`,
                  }}
                />
              ))}
            </GridCardContainer>
          </>
        )}
      </PageWrapper>
      <GearTagLine />
    </Layout>
  );
}

export const query = graphql`
  query blogPostsQuery(
    $skip: Int!
    $limit: Int!
    $shouldGetArticles: Boolean!
    $shouldGetAuthors: Boolean!
    $authorId: String
    $locale: String
    $blogCategoryId: String
  ) {
    posts: allSanityBlogPost(
      sort: { publishedDate: DESC }
      filter: {
        siteIdentifier: { eq: "sportdog" }
        localizedCategoriesValue: {
          elemMatch: { _id: { eq: $blogCategoryId } }
        }
        localizedAuthorValue: { _id: { eq: $authorId } }
        language: { eq: $locale }
      }
      skip: $skip
      limit: $limit
    ) @include(if: $shouldGetArticles) {
      nodes {
        id
        title
        description
        publishedDate
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
        author {
          title
        }
        slug {
          current
        }
        categories: localizedCategoriesValue {
          title
          slug {
            current
          }
        }
        SEOMetadata {
          title
          description
          canonicalUrl
          noIndex
          noFollow
          ogTitle
          ogDescription
          ogUrl
          ogImage {
            asset {
              url
            }
          }
        }
      }
    }

    authors: allSanityPerson(
      filter: {
        siteIdentifier: { eq: "sportdog" }
        relatedArticles: { elemMatch: { id: { ne: null } } }
        language: { eq: $locale }
      }
      skip: $skip
      limit: $limit
    ) @include(if: $shouldGetAuthors) {
      nodes {
        title
        slug {
          current
        }
        description
        image {
          asset: _rawAsset(resolveReferences: { maxDepth: 3 })
          crop: _rawCrop(resolveReferences: { maxDepth: 3 })
          hotspot: _rawHotspot(resolveReferences: { maxDepth: 3 })
          alt
        }
        relatedArticles {
          _id
        }
      }
    }

    people: allSanityPerson(
      filter: {
        siteIdentifier: { eq: "sportdog" }
        relatedArticles: { elemMatch: { id: { ne: null } } }
        language: { eq: $locale }
      }
    ) {
      nodes {
        _id
      }
    }

    categories: allSanityBlogCategory(
      filter: { siteIdentifier: { eq: "sportdog" }, language: { eq: $locale } }
    ) {
      nodes {
        title
        slug {
          current
        }
      }
    }
  }
`;

HuntingTrainingTipsTemplate.propTypes = {
  pageContext: PropTypes.shape({
    people: PropTypes.arrayOf(PropTypes.shape({})),
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
    skip: PropTypes.number,
    humanPageNumber: PropTypes.number,
    limit: PropTypes.number,
    previousPagePath: PropTypes.string,
    nextPagePath: PropTypes.string,
    blogAuthor: PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
    }),
    blogCategory: PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
    }),
    shouldGetAuthors: PropTypes.bool,
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
    pageCountByLanguage: PropTypes.shape({}),
  }).isRequired,

  data: PropTypes.shape({
    people: {
      nodes: PropTypes.arrayOf({}),
    },
    posts: {
      nodes: PropTypes.arrayOf({}),
    },
    authors: {
      nodes: PropTypes.arrayOf({}),
    },
    categories: {
      nodes: PropTypes.arrayOf({}),
    },
  }).isRequired,
};

export default HuntingTrainingTipsTemplate;
